import { type JSX,useCallback } from 'react';

import { Box, Stack } from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    selectCurrentEntity,
    selectCurrentTenantId
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES, ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { ClaimValueField } from 'common/components/sharedCustomComponents/Claims/ClaimValueField';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { useClaimMembershipDrawer } from 'common/hooks/useClaimMembershipDrawer';
import {
    APIUserClaimMembershipPostParams,
    APIUserClaimMembershipPostResponse,
    APIUserClaimMembershipPutParams,
    APIUserClaimMembershipPutResponse
} from 'common/services/api/UserClaimMembership/UserClaimMembership.api';
import { postUserProfileClaims, putUserProfileClaims } from 'common/services/api/UserProfileClaims/UserProfileClaims.thunk';
import { Claim, UserClaimMembership } from 'common/types/Entities';
import { DrawerEntitiesNames, EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { CLAIM_VALUE_NOT_CHANGED_ERROR_MESSAGE } from 'common/utils/claimErrorHandlers';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { ClaimNameDropDown } from 'features/roleClaimMembership/ClaimNameDropdown';
import { updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { selectUserDetails } from 'features/users/usersSlice';

interface UserProfileClaimItem {
    name: string;
    claimValue: string;
    claim: {
        id: string
    }
}

interface UserProfileClaimDrawerProperties {
    userId: string;
    tenant?: IUserProfileTenant;
    userFirstName: string;
    userLastName: string;
    userFullName: string;
}

function useUserProfileClaimDrawer(
    entityName: string,
    drawerProps: UserProfileClaimDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<UserClaimMembership & UserProfileClaimItem>['editingItemInfo']
) {
    const drawerContext = useDrawerContext<
        UserClaimMembership & UserProfileClaimItem,
        UserProfileClaimDrawerProperties,
        APIUserClaimMembershipPostParams,
        APIUserClaimMembershipPostResponse,
        APIUserClaimMembershipPutParams,
        APIUserClaimMembershipPutResponse
    >(entityName, drawerProps, editingItemInfo);

    const claimMembership = useClaimMembershipDrawer({
        drawerId: DrawerIDs.UserProfileClaims,
        currentId: drawerContext.currentId,
        isOpenDrawer: drawerContext.isOpenDrawer,
        action: drawerContext.action,
        editingClaimValue: editingItemInfo?.editingRow?.claimValue,
        entityType: 'Claim'
    });

    const getPostOptions = useCallback((): APIUserClaimMembershipPostParams => ({
        tenantId: drawerProps.tenant?.id ?? '',
        userId: drawerProps.userId,
        claimId: claimMembership.claim.id,
        value: claimMembership.claimValue,
    }), [drawerProps.tenant?.id, drawerProps.userId, claimMembership.claim.id, claimMembership.claimValue]);

    const getPutOptions = useCallback((): APIUserClaimMembershipPutParams => {
        claimMembership.setValueInConflict(false);
        return {
            tenantId: drawerProps.tenant?.id ?? '',
            userId: drawerProps.userId,
            claimId: editingItemInfo?.editingRow?.claim.id ?? '',
            value: claimMembership.claimValue || '',
        };
    }, [drawerProps.tenant?.id, drawerProps.userId, editingItemInfo?.editingRow?.claim.id, claimMembership.claimValue, claimMembership.setValueInConflict]);

    const createSuccessHandler = useCallback((postResult: APIUserClaimMembershipPostResponse) => {
        drawerContext.createSuccessHandler(postResult);
        updateUserProfileTenant(getPostOptions());
    }, [drawerContext, getPostOptions]);

    const editSuccessHandler = useCallback((putResult: APIUserClaimMembershipPutResponse) => {
        drawerContext.editSuccessHandler(putResult);
        updateUserProfileTenant(getPutOptions());
    }, [drawerContext, getPutOptions]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postUserProfileClaims, getPostOptions, createSuccessHandler, claimMembership.createFailureHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler, claimMembership.createFailureHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putUserProfileClaims, getPutOptions, editSuccessHandler, claimMembership.editFailureHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler, claimMembership.editFailureHandler]);

    return {
        ...drawerContext,
        claim: claimMembership.claim,
        claimValue: claimMembership.claimValue,
        valueInConflict: claimMembership.valueInConflict,
        handleClaimChange: claimMembership.handleClaimChange,
        handleClaimValueChange: claimMembership.handleClaimValueChange,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    claim,
    claimValue,
    valueInConflict,
    handleClaimChange,
    handleClaimValueChange,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    claim: Claim;
    claimValue: string;
    valueInConflict: boolean;
    handleClaimChange: (_e: unknown, newClaim: Claim) => void;
    handleClaimValueChange: (value: string) => void;
    postCallback: () => void;
    drawerProps: UserProfileClaimDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ClaimNameDropDown
                    value={claim}
                    onChange={handleClaimChange}
                    tenantId={drawerProps.tenant?.id ?? ''}
                    hasError={nameInConflict}
                />
                {nameInConflict &&
                    <ErrorHandler
                        errorMessage={'This Claim is already linked to this User.'}
                    />
                }
                <ReadOnlyTextField label="Name" value={drawerProps.userFullName} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenant?.name ?? ''} />
                <ClaimValueField
                    claim={claim}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                    hasError={valueInConflict}
                />
                {valueInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        The Claim value was not changed
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    claimValue,
    valueInConflict,
    handleClaimValueChange,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    claimValue: string;
    valueInConflict: boolean;
    handleClaimValueChange: (value: string) => void;
    putCallback: () => void;
    drawerProps: UserProfileClaimDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<UserClaimMembership & UserProfileClaimItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <ReadOnlyTextField label="Claim Name" value={editingItemInfo?.editingRow?.name} />
                <ReadOnlyTextField label="Name" value={drawerProps.userFullName} />
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenant?.name ?? ''} />
                <ClaimValueField
                    claim={editingItemInfo?.editingRow}
                    value={claimValue}
                    onChange={handleClaimValueChange}
                    hasError={valueInConflict}
                />
                {valueInConflict &&
                    <Box
                        component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        {CLAIM_VALUE_NOT_CHANGED_ERROR_MESSAGE}
                    </Box>
                }
            </Stack>
        </EditDrawer>
    );
}

export function UserProfileClaimMembershipCreateContent({
    title,
    tenant,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<UserClaimMembership & UserProfileClaimItem>>) {
    const userDetails = useAppSelector(selectUserDetails);
    const currentEntity = useAppSelector(selectCurrentEntity);
    const currentTenantId = useAppSelector(selectCurrentTenantId);
    const userFirstName = userDetails?.firstName ? userDetails.firstName : 'Unknown';
    const userLastName = userDetails?.lastName ? userDetails.lastName : 'User';
    const claimDrawerProps: UserProfileClaimDrawerProperties = {
        userId: userDetails?.userId ?? '',
        tenant,
        userFirstName: userDetails?.firstName ? userDetails.firstName : 'Unknown',
        userLastName: userDetails?.lastName ? userDetails.lastName : 'User',
        userFullName: `${userFirstName} ${userLastName}`
    };

    const drawerState = useUserProfileClaimDrawer(title, claimDrawerProps, editingItemInfo);

    if (currentEntity !== DrawerEntitiesNames.UserProfileClaims || currentTenantId !== tenant?.id) {
        return null;
    }

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                claim={drawerState.claim}
                claimValue={drawerState.claimValue}
                valueInConflict={drawerState.valueInConflict}
                handleClaimChange={drawerState.handleClaimChange}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                postCallback={drawerState.postCallback}
                drawerProps={claimDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                claimValue={drawerState.claimValue}
                valueInConflict={drawerState.valueInConflict}
                handleClaimValueChange={drawerState.handleClaimValueChange}
                putCallback={drawerState.putCallback}
                drawerProps={claimDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
